import { createRoot } from "react-dom/client";
import App from "./embed/App";
import styles from "./index.css?inline";
import { setupMonitoring } from "./monitoring";
import log from "./log";

// The main entry point for the botbrains embed script.
// We need to set height,width to 100% for inline embeds to expand correctly.
// The exact naming and ordering in DOM of `botbrains-shadow-host`, `botbrains-launcher`, and `botbrains-theme` is important for the CSS to work correctly.

export function botbrainsInitialize() {
  setupMonitoring();
  const host = document.createElement("aside");
  host.style.height = "100%";
  host.style.width = "100%";
  host.id = "botbrains-shadow-host";
  host.setAttribute("aria-label", "Chat widget");
  host.setAttribute("role", "complementary");
  document.body.appendChild(host);

  const shadowRoot = host.attachShadow({ mode: "open" });

  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    :host {
      all: initial;
      display: block; /* Ensure the host maintains block-level display */
    }
    ${styles}`;
  shadowRoot.appendChild(styleSheet);

  // Theming must be created under style reset, because tailwind preflight styles / fonts would be reset otherwise
  const container = document.createElement("div");
  container.id = "botbrains-launcher";
  container.className = "botbrains-theme";
  container.style.height = "100%";
  container.style.width = "100%";
  shadowRoot.appendChild(container);

  const root = createRoot(container);
  root.render(<App />);

  // Analytics is now lazy-initialized on first user interaction (see launcherSlice.ts and Chat.tsx)

  return () => {
    root.unmount();
    document.body.removeChild(host);
  };
}

function parseSleepParam() {
  const urlParams = new URLSearchParams(window.location.search);
  const sleepValue = urlParams.get("botbrainsDelay");
  return sleepValue ? parseInt(sleepValue, 10) : 0; // Default to 0 if not provided or invalid
}

// If window.botbrainsCleanup exists, call it before re-initializing
function safeInitialize() {
  if (typeof window.botbrainsCleanup === "function") {
    window.botbrainsCleanup();
  }

  const sleepDuration = parseSleepParam();
  log.debug(
    `[botbrains]: ${sleepDuration > 0 ? `Delaying initialization by ${sleepDuration}ms` : "Initializing immediately"}`
  );
  setTimeout(() => {
    window.botbrainsCleanup = botbrainsInitialize();
  }, sleepDuration);
}

// create only if botbrainsExplicitInitialization is not set
if (!window.botbrainsExplicitInitialization) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", safeInitialize);
  } else {
    safeInitialize();
  }
}

// Expose initialization function globally
window.botbrainsInitialize = botbrainsInitialize;
